import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Pin } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { PriorityBadge } from "@/components/public/Badges";
import { useI18n } from "@/lib/i18n";
import {
  useEvenementsDirect,
  useCreateEvenementDirect,
  useCloseEvenementDirect,
  useAddMiseAJourDirect,
} from "@/hooks/useEvenementsDirect";
import { adaptEvenementDirect } from "@/lib/adapters";
import type { Priority } from "@/lib/mock-data";
import type { BackendPriorite } from "@/types/evenements-direct.types";

const PRIORITY_TO_BACKEND: Record<Priority, BackendPriorite> = {
  confirmed: "CONFIRME",
  developing: "EN_DEVELOPPEMENT",
  correction: "CORRECTION",
  alert: "ALERTE",
};

const PRIORITIES: Priority[] = ["confirmed", "developing", "correction", "alert"];

function Live() {
  const { t, locale } = useI18n();
  const { data: dtos, isLoading } = useEvenementsDirect();

  const [showForm, setShowForm] = useState(false);
  const [titleFr, setTitleFr] = useState("");
  const [titleEn, setTitleEn] = useState("");

  // État des formulaires d'ajout de mise à jour, indexé par eventId
  const [updateTexts, setUpdateTexts] = useState<Record<number, string>>({});
  const [updatePriorities, setUpdatePriorities] = useState<Record<number, Priority>>({});
  const [updatePinneds, setUpdatePinneds] = useState<Record<number, boolean>>({});

  const createMutation = useCreateEvenementDirect();
  const closeMutation = useCloseEvenementDirect();
  const addUpdateMutation = useAddMiseAJourDirect();

  const events = (dtos ?? [])
    .map(adaptEvenementDirect)
    .sort((a, b) => (a.closed === b.closed ? 0 : a.closed ? 1 : -1));

  function handleCreate() {
    if (!titleFr.trim()) return;
    createMutation.mutate(
      { titre: { FR: titleFr, ...(titleEn.trim() ? { EN: titleEn } : {}) } },
      {
        onSuccess: () => {
          toast.success(t("admin.live.createSuccess"));
          setTitleFr("");
          setTitleEn("");
          setShowForm(false);
        },
        onError: () => toast.error(t("admin.common.saveError")),
      }
    );
  }

  function handleClose(id: string) {
    closeMutation.mutate(Number(id), {
      onSuccess: () => toast.success(t("admin.live.closeSuccess")),
      onError: () => toast.error(t("admin.common.saveError")),
    });
  }

  function handleAddUpdate(eventId: string) {
    const id = Number(eventId);
    const text = updateTexts[id] ?? "";
    const priority = updatePriorities[id] ?? "confirmed";
    const pinned = updatePinneds[id] ?? false;
    if (!text.trim()) return;

    addUpdateMutation.mutate(
      {
        id,
        payload: {
          priorite: PRIORITY_TO_BACKEND[priority],
          epinglee: pinned,
          texte: { FR: text },
        },
      },
      {
        onSuccess: () => {
          toast.success(t("admin.live.updateSuccess"));
          setUpdateTexts((prev) => ({ ...prev, [id]: "" }));
          setUpdatePinneds((prev) => ({ ...prev, [id]: false }));
        },
        onError: () => toast.error(t("admin.common.saveError")),
      }
    );
  }

  return (
    <>
      <AdminTopbar title={t("admin.live.title")} subtitle={t("admin.live.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Barre : titre de section + bouton nouveau direct */}
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("admin.live.eventsList")}
          </h2>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-2 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background hover:opacity-90"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {t("admin.live.newEvent")}
          </button>
        </div>

        {/* Formulaire de création */}
        {showForm && (
          <div className="rounded-md border border-border bg-card p-5 space-y-4">
            <div className="space-y-3">
              <input
                type="text"
                placeholder={t("admin.live.titleFr")}
                value={titleFr}
                onChange={(e) => setTitleFr(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <input
                type="text"
                placeholder={t("admin.live.titleEn")}
                value={titleEn}
                onChange={(e) => setTitleEn(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={!titleFr.trim() || createMutation.isPending}
                className="rounded-md bg-verified px-3 py-1.5 text-xs font-semibold text-verified-foreground disabled:opacity-50"
              >
                {createMutation.isPending ? t("admin.common.loading") : t("admin.live.create")}
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
              >
                {t("admin.common.cancel")}
              </button>
            </div>
          </div>
        )}

        {isLoading && (
          <p className="text-sm text-muted-foreground">{t("admin.common.loading")}</p>
        )}

        {!isLoading && events.length === 0 && (
          <p className="text-sm text-muted-foreground">{t("admin.live.noEvents")}</p>
        )}

        {/* Liste des événements */}
        {events.map((event) => {
          const id = Number(event.id);
          const title =
            (event.title as Record<string, string>)[locale] ??
            (event.title as Record<string, string>).fr ??
            "";
          const updateText = updateTexts[id] ?? "";
          const updatePriority = updatePriorities[id] ?? ("confirmed" as Priority);
          const pinned = updatePinneds[id] ?? false;

          return (
            <ModulePanel
              key={event.id}
              title={title}
              description={
                event.closed
                  ? t("admin.live.closed")
                  : `🔴 ${t("live.badge")} · ${t("admin.live.updates", { count: event.updates.length })}`
              }
              action={
                !event.closed ? (
                  <button
                    onClick={() => handleClose(event.id)}
                    disabled={closeMutation.isPending}
                    className="rounded-md bg-alert px-3 py-1.5 text-xs font-semibold text-alert-foreground disabled:opacity-50"
                  >
                    {t("admin.live.close")}
                  </button>
                ) : undefined
              }
            >
              <div className="space-y-4">
                {/* Formulaire ajout mise à jour (actif uniquement) */}
                {!event.closed && (
                  <div className="space-y-3 border-b border-border pb-5">
                    <textarea
                      placeholder={t("admin.live.newUpdate")}
                      value={updateText}
                      onChange={(e) =>
                        setUpdateTexts((prev) => ({ ...prev, [id]: e.target.value }))
                      }
                      className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm"
                      rows={3}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {PRIORITIES.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() =>
                            setUpdatePriorities((prev) => ({ ...prev, [id]: p }))
                          }
                          className={`rounded-md border px-2 py-1.5 transition-all ${
                            updatePriority === p
                              ? "ring-2 ring-foreground/30"
                              : "opacity-50"
                          }`}
                        >
                          <PriorityBadge priority={p} />
                        </button>
                      ))}
                      <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={pinned}
                          onChange={(e) =>
                            setUpdatePinneds((prev) => ({
                              ...prev,
                              [id]: e.target.checked,
                            }))
                          }
                          className="rounded"
                        />
                        <Pin className="h-3 w-3" />
                        {t("admin.live.pinned")}
                      </label>
                      <button
                        onClick={() => handleAddUpdate(event.id)}
                        disabled={!updateText.trim() || addUpdateMutation.isPending}
                        className="ml-auto rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background disabled:opacity-50"
                      >
                        {addUpdateMutation.isPending
                          ? t("admin.common.loading")
                          : t("admin.live.publishUpdate")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Liste des mises à jour */}
                {event.updates.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("live.noUpdates")}</p>
                ) : (
                  <ul className="space-y-3 text-sm">
                    {event.updates.map((u) => (
                      <li
                        key={u.id}
                        className="rounded-md border border-border bg-surface p-3"
                      >
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono">
                            {new Date(u.timestamp).toLocaleTimeString(
                              locale === "fr" ? "fr-FR" : "en-GB"
                            )}
                          </span>
                          <PriorityBadge priority={u.priority} />
                          {u.pinned && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                              <Pin className="h-2.5 w-2.5" />
                            </span>
                          )}
                        </div>
                        <p className="mt-1.5">
                          {(u.text as Record<string, string>)[locale] ??
                            (u.text as Record<string, string>).fr}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </ModulePanel>
          );
        })}
      </div>
    </>
  );
}

export default Live;
